<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { onClickOutside, useMagicKeys } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    modelValue: string[]
    options: string[]
    placeholder?: string
    emptyMessage?: string
  }>(),
  {
    placeholder: 'Search...',
    emptyMessage: 'No results found',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const MAX_RESULTS = 20

const sorted = computed(() => [...props.options].sort((a, b) => a.localeCompare(b)))

///////////////////////////////////////////////////////////////////////////////
// Search
///////////////////////////////////////////////////////////////////////////////

const query = ref('')
const isOpen = ref(false)
const highlightedIndex = ref(-1)

const results = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (q.length < 2) return []
  return sorted.value.filter((item) => item.toLowerCase().includes(q)).slice(0, MAX_RESULTS)
})

watch(query, (q) => {
  isOpen.value = q.trim().length >= 2
  highlightedIndex.value = -1
})

///////////////////////////////////////////////////////////////////////////////
// Selection
///////////////////////////////////////////////////////////////////////////////

const select = (item: string) => {
  query.value = ''
  isOpen.value = false
  highlightedIndex.value = -1
  emit('update:modelValue', [...props.modelValue, item])
}

const remove = (item: string) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((v) => v !== item),
  )
}

///////////////////////////////////////////////////////////////////////////////
// Keyboard Navigation
///////////////////////////////////////////////////////////////////////////////

const wrapper = ref<HTMLElement | null>(null)
const resultsList = ref<HTMLElement | null>(null)

onClickOutside(wrapper, () => {
  isOpen.value = false
})

const { escape } = useMagicKeys()
watch(escape, (v) => {
  if (v) isOpen.value = false
})

const findNextSelectable = (from: number, direction: 1 | -1): number => {
  let idx = from
  while (idx >= 0 && idx < results.value.length) {
    if (!props.modelValue.includes(results.value[idx])) return idx
    idx += direction
  }
  return highlightedIndex.value
}

const onKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value || !results.value.length) return

  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      highlightedIndex.value = findNextSelectable(highlightedIndex.value + 1, 1)
      scrollToHighlighted()
      break
    case 'ArrowUp':
      e.preventDefault()
      highlightedIndex.value = findNextSelectable(highlightedIndex.value - 1, -1)
      scrollToHighlighted()
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex.value >= 0 && highlightedIndex.value < results.value.length) {
        const item = results.value[highlightedIndex.value]
        if (!props.modelValue.includes(item)) select(item)
      }
      break
  }
}

const scrollToHighlighted = () => {
  nextTick(() => {
    const list = resultsList.value
    if (!list) return
    const el = list.children[highlightedIndex.value] as HTMLElement
    if (el) el.scrollIntoView({ block: 'nearest' })
  })
}
</script>

<template>
  <div ref="wrapper" class="relative">
    <div class="relative">
      <input
        type="text"
        v-model="query"
        class="w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm shadow-sm focus:border-sky-300 focus:ring focus:ring-sky-200 focus:ring-opacity-50"
        :placeholder="placeholder"
        @keydown="onKeydown"
        @focus="isOpen = query.trim().length >= 2"
      />
    </div>

    <ul
      v-show="isOpen && results.length"
      ref="resultsList"
      class="absolute z-50 mt-1 max-h-[280px] w-full min-w-[220px] divide-y-0 divide-gray-100 overflow-y-auto rounded-md bg-white shadow-lg ring-[1px] ring-gray-700/20"
    >
      <li
        v-for="(item, idx) in results"
        :key="item"
        class="relative flex items-center whitespace-nowrap px-3 py-1.5 text-sm"
        :class="[
          modelValue.includes(item) ? 'bg-gray-100 text-gray-400' : 'cursor-pointer text-gray-700 hover:bg-gray-100',
          idx === highlightedIndex && !modelValue.includes(item) ? 'bg-gray-100' : '',
        ]"
        @click="modelValue.includes(item) ? null : select(item)"
        @mouseenter="highlightedIndex = idx"
      >
        <div class="font-medium">{{ item }}</div>
      </li>
    </ul>

    <div
      v-if="isOpen && !results.length && query.trim().length >= 2"
      class="absolute z-50 mt-1 w-full rounded-md bg-white px-3 py-2.5 text-sm text-gray-400 shadow-lg ring-[1px] ring-gray-700/20"
    >
      {{ emptyMessage }}
    </div>

    <div v-if="modelValue.length" class="mt-3 flex flex-wrap gap-2">
      <span
        v-for="item in modelValue"
        :key="item"
        class="inline-flex cursor-default items-center rounded-full border-none bg-[#009192]/[0.08] py-1.5 pl-3.5 pr-3 text-[13px] font-medium text-[#009192] shadow-sm ring-1 ring-[#009192]"
      >
        {{ item }}
        <button
          type="button"
          class="-mt-[2px] ml-2 cursor-pointer border-none bg-transparent p-0 text-base leading-none text-[#009192]/60 hover:text-[#009192]"
          @click="remove(item)"
        >
          &times;
        </button>
      </span>
    </div>
  </div>
</template>
